import * as React from "react"
import {
  List,
  ListItem,
  ListItemActions,
  ListItemContent,
} from "@modules/ui/components/List"
import { Button, Spacer, ThemeCss, VisuallyHidden } from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import { Integration } from "@modules/ui/components/Integration"
import { CdnIntegrationDropdown } from "@modules/cdn/public-target/CdnIntegrationDropdown"
import { CdnIntegration, CdnVendor } from "@modules/graphql/types"
import { HostingDetails } from "@modules/cdn/gatsbyCloudCdn"
import { cdnConfigLabels, cdnVendorLogo, cdnVendorNames } from "../constants"
import { isProperlyConnected } from "@modules/site/cdnIntegrations"
import { cdnIntegration as cdnIntegrationText } from "@modules/locales/default.js"
import { interpolateMessage } from "@modules/locales"

const listItemContentCss: ThemeCss = theme => ({
  padding: `${theme.space[7]} ${theme.space[10]}`,
})

const listItemContentColumnCss: ThemeCss = theme => ({
  display: "flex",
  flexDirection: "column",

  [theme.mediaQueries.phablet]: {
    flexDirection: "row",
  },
})

const listItemActionsCss: ThemeCss = theme => ({ marginRight: theme.space[10] })

export type CdnIntegrationsListProps = {
  siteId: string
  integrations: CdnIntegration[]
  onSelect: (cdnIntegration: CdnIntegration) => void
  onDelete: (cdnIntegration: CdnIntegration) => void
  allowToEdit?: boolean
}

export function CdnIntegrationsList({
  siteId,
  integrations = [],
  onSelect,
  onDelete,
  allowToEdit,
}: CdnIntegrationsListProps) {
  return (
    <List>
      {integrations.map(cdn => {
        if (cdn.vendor === CdnVendor.CloudCdn) {
          return null
        }

        const isConnected = isProperlyConnected(cdn)

        return (
          <ListItem key={cdn.vendor}>
            <ListItemContent>
              <div css={listItemContentCss}>
                <div css={listItemContentColumnCss}>
                  <Integration.Vendor
                    image={cdnVendorLogo[cdn.vendor]}
                    name={cdn.vendor}
                  />

                  <VisuallyHidden>{cdnVendorNames[cdn.vendor]}</VisuallyHidden>

                  <Integration.Status
                    connected={isConnected}
                    data-testid={`${cdn.vendor}-status`}
                  />
                </div>

                {isConnected && (
                  <Integration.Details data-testid={`${cdn.vendor}-details`}>
                    {/**
                     * Transform and format CDN configuration for display:
                     *   - parse the config object into an array of [key, value] pairs
                     *   - filter the array so that it only includes pairs where the key has a label defined
                     *   - display the kay label and the value
                     */}
                    {Object.entries(cdn.config)
                      .filter(([configItemKey]) =>
                        Boolean(cdnConfigLabels[cdn.vendor][configItemKey])
                      )
                      .map(([configItemKey, configItemValue]) => (
                        <Integration.Detail key={configItemKey}>
                          <Integration.Label>
                            {cdnConfigLabels[cdn.vendor][configItemKey]}
                          </Integration.Label>
                          <Integration.Value>
                            {configItemValue}
                          </Integration.Value>
                        </Integration.Detail>
                      ))}
                  </Integration.Details>
                )}
                {isConnected && cdn.vendor === CdnVendor.Fastly ? (
                  <React.Fragment>
                    <Spacer size={5} />
                    <HostingDetails siteId={siteId} />
                  </React.Fragment>
                ) : null}
              </div>
            </ListItemContent>

            <ListItemActions>
              <div css={listItemActionsCss}>
                {isConnected && allowToEdit && (
                  <CdnIntegrationDropdown
                    data-testid={`${cdn.vendor}-edit-dropdown`}
                    onEdit={() => onSelect(cdn)}
                    onDelete={() => onDelete(cdn)}
                    aria-label={interpolateMessage<"cdnName">(
                      cdnIntegrationText.actions.manageCdn,
                      { cdnName: cdnVendorNames[cdn.vendor] }
                    )}
                  />
                )}

                {!isConnected && allowToEdit && (
                  <Button
                    variant="GHOST"
                    rightIcon={<MdArrowForward />}
                    onClick={() => onSelect(cdn)}
                    aria-label={interpolateMessage<"cdnName">(
                      cdnIntegrationText.actions.connectCdn,
                      { cdnName: cdnVendorNames[cdn.vendor] }
                    )}
                  >
                    {cdnIntegrationText.actions.connectShort}
                  </Button>
                )}
              </div>
            </ListItemActions>
          </ListItem>
        )
      })}
    </List>
  )
}
